import React, { useState, useEffect, useCallback, useRef } from 'react'
import styled from 'styled-components'

interface CarouselProps {
  items: {
    id: string | number
    content: React.ReactNode
  }[]
  autoPlay?: boolean
  interval?: number
  showDots?: boolean
  showArrows?: boolean
  className?: string
}

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: visible;
  -webkit-user-select: none;
  user-select: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
`

const CarouselArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 80vw;
  max-width: 1200px;
  min-width: 320px;
  margin: 0 auto;
  position: relative;
  height: 100%;
  touch-action: pan-y;
  cursor: grab;
`

const getItemStyle = (pos: number) => {
  // pos: -2, -1, 0, 1, 2
  switch (pos) {
    case 0:
      return {
        scale: 1.08,
        translate: '0',
        opacity: 1,
        z: 3,
        blur: 0,
        border: 'none',
        shadow: '0 8px 32px rgba(0,0,0,0.18)',
      }
    case -1:
      return {
        scale: 0.92,
        translate: '-18%',
        opacity: 0.85,
        z: 2,
        blur: 0,
        border: 'none',
        shadow: 'none',
      }
    case 1:
      return {
        scale: 0.92,
        translate: '18%',
        opacity: 0.85,
        z: 2,
        blur: 0,
        border: 'none',
        shadow: 'none',
      }
    case -2:
      return {
        scale: 0.85,
        translate: '-36%',
        opacity: 0.55,
        z: 1,
        blur: 0,
        border: 'none',
        shadow: 'none',
      }
    case 2:
      return {
        scale: 0.85,
        translate: '36%',
        opacity: 0.55,
        z: 1,
        blur: 0,
        border: 'none',
        shadow: 'none',
      }
    default:
      return {
        scale: 0.7,
        translate: '0',
        opacity: 0,
        z: 0,
        blur: 0,
        border: 'none',
        shadow: 'none',
      }
  }
}

const Item = styled.div<{
  $pos: number // -2, -1, 0, 1, 2
}>`
  flex: 0 0 20%;
  max-width: 20%;
  min-width: 0;
  height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.5s, filter 0.5s,
    border 0.5s;
  cursor: pointer;
  z-index: ${({ $pos }) => getItemStyle($pos).z};
  transform: ${({ $pos }) =>
    `scale(${getItemStyle($pos).scale}) translateX(${
      getItemStyle($pos).translate
    })`};
  opacity: ${({ $pos }) => getItemStyle($pos).opacity};
  box-shadow: ${({ $pos }) => getItemStyle($pos).shadow};
  border: ${({ $pos }) => getItemStyle($pos).border};
  filter: ${({ $pos }) =>
    getItemStyle($pos).blur ? `blur(${getItemStyle($pos).blur}px)` : 'none'};
  background: transparent;
  margin: 0 0.5vw;
  pointer-events: ${({ $pos }) => ($pos === 0 ? 'auto' : 'auto')};
`

const ArrowButton = styled.button<{ $direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background-color: rgba(0, 0, 0, 0.75);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  font-weight: bold;
  z-index: 10;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  ${(props) => (props.$direction === 'left' ? 'left: 20px;' : 'right: 20px;')}

  &:hover {
    background-color: rgba(0, 0, 0, 0.9);
    transform: translateY(-50%) scale(1.15);
    border-color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`

const DotsContainer = styled.div`
  position: relative;
  margin: 1.5rem 0 0 0;
  display: flex;
  gap: 10px;
  z-index: 3;
  justify-content: center;
  align-items: center;
`

const Dot = styled.button<{ $isActive: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.$isActive ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
  transform: ${(props) => (props.$isActive ? 'scale(1.2)' : 'scale(1)')};
  &:hover {
    background-color: rgba(255, 255, 255, 0.8);
    transform: scale(1.1);
  }
`

const Carousel: React.FC<CarouselProps> = ({
  items,
  autoPlay = true,
  interval = 1000,
  showDots = true,
  showArrows = true,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)
  const [mouseDown, setMouseDown] = useState(false)
  const [mouseStartX, setMouseStartX] = useState<number | null>(null)
  const [mouseEndX, setMouseEndX] = useState<number | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const minSwipeDistance = 50
  const total = items.length

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index)
  }, [])

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % total)
  }, [total])

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + total) % total)
  }, [total])

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (isAutoPlaying) {
      timer = setInterval(nextSlide, interval)
    }
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [isAutoPlaying, interval, nextSlide])

  // Touch/swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (touchStart === null || touchEnd === null) return
    const distance = touchStart - touchEnd
    if (distance > minSwipeDistance) {
      nextSlide()
    }
    if (distance < -minSwipeDistance) {
      prevSlide()
    }
  }

  // Mouse drag handlers for desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    setMouseDown(true)
    setMouseStartX(e.clientX)
    setMouseEndX(null)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!mouseDown) return
    setMouseEndX(e.clientX)
  }

  const handleMouseUp = () => {
    if (!mouseDown || mouseStartX === null || mouseEndX === null) {
      setMouseDown(false)
      return
    }
    const distance = mouseStartX - mouseEndX
    if (distance > minSwipeDistance) {
      nextSlide()
    }
    if (distance < -minSwipeDistance) {
      prevSlide()
    }
    setMouseDown(false)
    setMouseStartX(null)
    setMouseEndX(null)
  }

  // Get indices for prev-2, prev-1, current, next+1, next+2 (circular)
  const getIndex = (offset: number) => (currentIndex + offset + total) % total
  const visibleOffsets = [-2, -1, 0, 1, 2]

  // Smooth step-through animation to a target index
  const animateToIndex = (targetIndex: number) => {
    if (targetIndex === currentIndex) return
    let diff = (targetIndex - currentIndex + total) % total
    if (diff > total / 2) diff -= total // go the shortest way
    const step = diff > 0 ? 1 : -1
    let steps = Math.abs(diff)
    let i = 0
    const animateStep = () => {
      setCurrentIndex((prev) => (prev + step + total) % total)
      i++
      if (i < steps) {
        setTimeout(animateStep, 120) // adjust speed as needed
      }
    }
    animateStep()
  }

  return (
    <Container
      className={className}
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(autoPlay)}>
      <CarouselArea
        ref={contentRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => setMouseDown(false)}
        style={{ cursor: mouseDown ? 'grabbing' : 'grab' }}>
        {showArrows && (
          <>
            <ArrowButton
              $direction='left'
              onClick={prevSlide}
              aria-label='Previous slide'>
              &#10094;
            </ArrowButton>
            <ArrowButton
              $direction='right'
              onClick={nextSlide}
              aria-label='Next slide'>
              &#10095;
            </ArrowButton>
          </>
        )}
        {visibleOffsets.map((offset) => {
          const idx = getIndex(offset)
          return (
            <Item
              key={items[idx].id}
              $pos={offset}
              onClick={() => {
                if (offset !== 0) animateToIndex(idx)
              }}>
              {items[idx].content}
            </Item>
          )
        })}
      </CarouselArea>
      {showDots && (
        <DotsContainer>
          {items.map((_, index) => (
            <Dot
              key={index}
              $isActive={index === currentIndex}
              onClick={() => animateToIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </DotsContainer>
      )}
    </Container>
  )
}

export default Carousel
